import { AppSpinner } from '@components/app-spinner';
import { CustomSnackbarGlobal } from '@components/custom-snackbar-global';
import Navigation from '@components/Navigation';
import { ProtectedRoute } from '@components/route';
import { CssBaseline } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { Redirect, Router } from '@reach/router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import { SnackbarProvider } from 'material-ui-snackbar-provider';
import { Fragment, ReactNode, Suspense } from 'react';
import { OverlayProvider } from 'react-aria';
import { RecoilRoot, useRecoilValue } from 'recoil';
import { AppLoadingIndicator } from 'src/components/AppLoadingIndicator';
import MfaLoginErrorCleanup from 'src/components/MfaLoginErrorCleanup';
import ScrollToTop from 'src/components/ScrollToTop';
import { RoutePaths } from 'src/constants';
import WizardPopup from 'src/containers/WizardPopup';
import { useCustomerMode } from 'src/hooks/useCustomerMode';
import { ExtendedUserSnapProvider } from 'src/hooks/useExtendUsersnap';
import { useGTM } from 'src/hooks/useGTM';
import { ValuationSettingsProvider, useValuationSettings } from 'src/hooks/useValuationSettings';
import { WizardProvider } from 'src/hooks/useWizard';
import { Usersnap } from 'src/lib/Usersnap';
import { ValuationCountryCode } from 'src/modules/generated/api';
import { HelpIndexPage } from 'src/pages/help';
import { CategoryPage } from 'src/pages/help/[category]';
import { AnswerPage } from 'src/pages/help/[category]/[answer]';
import { RouteDef, routes } from 'src/routes';
import { vgrdTheme } from 'src/setup/customer-mode-themes';
import theme from 'src/setup/theme';
import { useAuthenticationStore } from 'src/stores/Authentication';
import { customerModeActiveState } from 'src/stores/customer-mode';

import PacketsPage from './pages/settings/packets';
import PacketDetailPage from './pages/settings/packets/[id]';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) =>
        !(axios.isAxiosError(error) && error.response?.status === 401) && failureCount < 3,
    },
  },
});

const renderRoute = (route: RouteDef): JSX.Element => {
  const { element: Element, path, ...rest } = route;

  if (route.protected) {
    return <ProtectedRoute key={path} component={Element} path={path} {...rest} />;
  }

  return <Element key={path} path={path} {...rest} />;
};

const CustomerModeThemeContent = ({ children }: { children: ReactNode }) => {
  const { customerModeActive } = useCustomerMode();
  const { valuationCountry } = useValuationSettings();
  const isVgrdBranding = customerModeActive && valuationCountry === ValuationCountryCode.De;

  return (
    <ThemeProvider theme={isVgrdBranding ? vgrdTheme : theme}>
      <CssBaseline />
      {/* MUI v4 keys its JSS sheets by theme identity, so a live theme swap only re-attaches sheets for
          components that happen to re-render. Ones that don't keep their old class names *and* keep the
          old sheet attached through a stale refcount, so they stay in the previous branding until the
          next full reload. Remounting on branding change is the only reliable way to re-attach every
          sheet. It costs the subtree's in-memory state (scroll position, open dialogs), which is
          acceptable for a deliberate, rare toggle. */}
      <Fragment key={isVgrdBranding ? 'vgrd' : 'default'}>{children}</Fragment>
    </ThemeProvider>
  );
};

// Both branding inputs are synchronous — a persisted Recoil atom and a localStorage-backed context —
// so the fallback can render the branded theme right away instead of flashing GW Scout orange at a
// customer on every cold load. It deliberately skips the CAN_USE_CUSTOMER_MODE check that makes
// useCustomerMode() suspend, so this is an optimistic read of the user's last known intent.
const CustomerModeThemeFallback = () => {
  const customerModeActive = useRecoilValue(customerModeActiveState);
  const { valuationCountry } = useValuationSettings();
  const activeTheme = customerModeActive && valuationCountry === ValuationCountryCode.De ? vgrdTheme : theme;

  return (
    <ThemeProvider theme={activeTheme}>
      <CssBaseline />
      <AppSpinner />
    </ThemeProvider>
  );
};

// useCustomerMode() reads a suspense-mode query (useUserPermissions -> useUser), so this needs its
// own Suspense boundary — it renders above the app's main <Suspense fallback={<AppSpinner />}>.
// The fallback must NOT re-render `children` (Navigation/Router/Usersnap/...): since this boundary
// commits on every cold load (the query is always uncached at first), doing so would mount that
// whole subtree twice (once as fallback, once for real), double-firing effects like the Usersnap
// script injection.
const CustomerModeThemeWrapper = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<CustomerModeThemeFallback />}>
    <CustomerModeThemeContent>{children}</CustomerModeThemeContent>
  </Suspense>
);

const App = () => {
  const {
    state: { hasAccess },
  } = useAuthenticationStore();

  useGTM();

  return (
    <QueryClientProvider client={queryClient}>
      <RecoilRoot>
        <ValuationSettingsProvider>
          <CustomerModeThemeWrapper>
            <SnackbarProvider SnackbarComponent={CustomSnackbarGlobal}>
              <ExtendedUserSnapProvider>
                <WizardProvider hasReallyAccess={hasAccess}>
                  <OverlayProvider id="aria-container">
                    <Suspense fallback={<AppSpinner />}>
                      <Usersnap />
                      <Navigation />
                      <WizardPopup />
                      <ScrollToTop />
                      <MfaLoginErrorCleanup />
                      <Router>
                        {routes.map(renderRoute)}

                        <HelpIndexPage path="/help">
                          <CategoryPage path=":category" />
                          <AnswerPage path=":category/:answer" />
                        </HelpIndexPage>

                        <PacketsPage path={RoutePaths.packets}>
                          <PacketDetailPage path=":id" />
                        </PacketsPage>

                        {/* Silently redirect 404 errors to cars page */}
                        <Redirect from="*" to={RoutePaths.cars} noThrow />
                      </Router>
                    </Suspense>
                    <AppLoadingIndicator />
                  </OverlayProvider>
                </WizardProvider>
              </ExtendedUserSnapProvider>
            </SnackbarProvider>
          </CustomerModeThemeWrapper>
        </ValuationSettingsProvider>
      </RecoilRoot>
    </QueryClientProvider>
  );
};

export default App;
